/**
 * Linear turnover wizard — Slice 18 / 42 order:
 * Start → Keys → Walk → [Check] → [Maint] → Clean → Inventory → After → Drop keys → Done
 */

import {
  SIMPLE_TURNOVER_V1,
  isWalkComplete,
  isWalkQuickChecksComplete,
  isSimpleDropKeysComplete,
  isMaintenanceChecksComplete,
  readSimpleFlowState,
  simpleFlowSegments,
  type SimpleFlowChromeOpts,
} from "@/lib/simpleTurnover";

export type TurnoverWizardStepId =
  | "start_visit"
  | "arrival_check"
  | "keys"
  | "walk"
  | "walk_checks"
  | "maintenance"
  | "before_photos"
  | "inspect"
  | "clean_free"
  | "after_photos"
  | "drop_keys"
  | "cleaning"
  | "inventory_guest"
  | "inventory_supply"
  | "inventory_single"
  | "guest_setup"
  | "done";

export interface TurnoverWizardStep {
  id: TurnoverWizardStepId;
  labelKey: string;
  detailKey?: string;
  complete: boolean;
}

export interface TurnoverWizardContext {
  status: string | null | undefined;
  startedAt: string | null | undefined;
  entryWalkthroughCompleted: boolean | undefined;
  checklistCompleted: boolean | null | undefined;
  inventoryCompleted: boolean | null | undefined;
  inventoryGuestPhaseCompleted: boolean | undefined;
  inventorySupplyPhaseCompleted: boolean | undefined;
  includeInventoryCheck: boolean | undefined;
  postCleanInventoryCount: number;
  guestAmenitiesCount: number;
  supplyClosetCount: number;
  cleaningFlowState: "checklist" | "inventory" | "complete";
  entryWalkthroughState?: unknown;
  closeoutKeysConfirmed?: boolean | null;
  /** Slice 40 — property_preventative_maintenance ids due for cleaner on this job. */
  dueMaintenanceCheckIds?: string[];
  /** Slice 42 — property_walk_quick_checks ids for this property (empty = no Check step). */
  walkQuickCheckIds?: string[];
  /** Slice 44 — guest amenity staging lines (empty = no Guest step). */
  guestStagingLineCount?: number;
  guestSetupCompleted?: boolean;
}

function inventoryEnabled(ctx: TurnoverWizardContext): boolean {
  return ctx.includeInventoryCheck !== false && ctx.postCleanInventoryCount > 0;
}

export function buildTurnoverWizardSteps(ctx: TurnoverWizardContext): TurnoverWizardStep[] {
  const steps: TurnoverWizardStep[] = [];
  if (ctx.status !== "in_progress") return steps;

  const started = !!ctx.startedAt;
  const inv = inventoryEnabled(ctx);

  if (SIMPLE_TURNOVER_V1) {
    const simple = readSimpleFlowState(ctx.entryWalkthroughState);
    const keysDone = !!simple.keys_complete;
    const walkDone = isWalkComplete(simple);
    const checkIds = ctx.walkQuickCheckIds ?? [];
    const includeCheck = checkIds.length > 0;
    const checksDone = !includeCheck || isWalkQuickChecksComplete(simple, checkIds);
    const dueMaintIds = ctx.dueMaintenanceCheckIds ?? [];
    const includeMaint = dueMaintIds.length > 0;
    const maintDone = !includeMaint || isMaintenanceChecksComplete(simple, dueMaintIds);
    const cleanDone = !!simple.clean_complete;
    const afterDone = !!ctx.checklistCompleted;
    const dropDone = isSimpleDropKeysComplete(simple);
    const inventoryDone =
      !!ctx.inventoryCompleted ||
      ctx.includeInventoryCheck === false ||
      (ctx.postCleanInventoryCount === 0 && cleanDone);

    steps.push({ id: "start_visit", labelKey: "Start", complete: started });
    steps.push({ id: "keys", labelKey: "Keys", complete: keysDone });
    steps.push({ id: "walk", labelKey: "Walk", detailKey: "Photos", complete: walkDone });
    if (includeCheck) {
      steps.push({ id: "walk_checks", labelKey: "Check", complete: checksDone });
    }
    if (includeMaint) {
      steps.push({ id: "maintenance", labelKey: "Maintenance", complete: maintDone });
    }
    steps.push({ id: "clean_free", labelKey: "Clean", complete: cleanDone });
    const guestLines = ctx.guestStagingLineCount ?? 0;
    const includeGuest = guestLines > 0;
    const guestDone = !includeGuest || !!ctx.guestSetupCompleted;
    if (includeGuest) {
      steps.push({ id: "guest_setup", labelKey: "Guest", complete: guestDone });
    }
    steps.push({ id: "inventory_single", labelKey: "Inventory", complete: inventoryDone });
    steps.push({ id: "after_photos", labelKey: "After", detailKey: "Photos", complete: afterDone });
    steps.push({ id: "drop_keys", labelKey: "Drop", detailKey: "Keys", complete: dropDone });
    steps.push({
      id: "done",
      labelKey: "Done",
      complete:
        ctx.cleaningFlowState === "complete" &&
        inventoryDone &&
        afterDone &&
        dropDone &&
        walkDone &&
        checksDone &&
        cleanDone &&
        guestDone &&
        maintDone &&
        keysDone &&
        started,
    });
    return steps;
  }

  const entryDone = !!ctx.entryWalkthroughCompleted;
  const checklistDone = !!ctx.checklistCompleted;

  steps.push({ id: "start_visit", labelKey: "Start your visit", complete: started });
  steps.push({
    id: "arrival_check",
    labelKey: "Arrival & unit check",
    detailKey: "Keys, access, and how the guest left (before cleaning)",
    complete: entryDone,
  });
  steps.push({
    id: "cleaning",
    labelKey: "Cleaning checklist",
    detailKey: "After photos show the unit after you clean",
    complete: checklistDone,
  });
  if (inv) {
    steps.push({
      id: "inventory_single",
      labelKey: "Inventory & closeout",
      detailKey: "Restock counts and keys returned before you finish",
      complete: !!ctx.inventoryCompleted,
    });
  }
  steps.push({
    id: "done",
    labelKey: "Turnover complete",
    complete: ctx.cleaningFlowState === "complete" && (!inv || !!ctx.inventoryCompleted) && checklistDone && entryDone && started,
  });

  return steps;
}

export function getActiveWizardStepId(steps: TurnoverWizardStep[]): TurnoverWizardStepId {
  for (const s of steps) {
    if (!s.complete) return s.id;
  }
  return "done";
}

export function getWizardStepIndex(steps: TurnoverWizardStep[], id: TurnoverWizardStepId): number {
  const i = steps.findIndex((s) => s.id === id);
  return i < 0 ? 0 : i;
}

/** Progress segment index — pass chrome opts when Check / Maint segments are present. */
export function simpleFlowSegmentIndex(
  stepId: TurnoverWizardStepId | string,
  opts?: boolean | SimpleFlowChromeOpts,
): number {
  const segs = simpleFlowSegments(opts);
  const normalized =
    stepId === "before_photos" || stepId === "inspect" ? "walk" : stepId;
  const i = segs.indexOf(normalized as (typeof segs)[number]);
  return i;
}
